import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { ChatMessage, TypingIndicator, ChatState } from "../components/chatTypes";

const TYPING_DEBOUNCE_MS = 1000;
const MESSAGES_PAGE_SIZE = 30;
const CHAT_BUCKET = "chat-attachments";
const SIGNED_URL_TTL_SECONDS = 60 * 60;

const decorateMessages = async (msgs: ChatMessage[]): Promise<ChatMessage[]> => {
  const withAttachment = msgs.filter((m) => m.attachment_url);
  if (withAttachment.length === 0) {
    return msgs.map((m) => ({ ...m, signed_url: null }));
  }

  const paths = withAttachment.map((m) => m.attachment_url as string);
  const { data: signed } = await supabase.storage
    .from(CHAT_BUCKET)
    .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS);

  const byPath = new Map<string, string | null>();
  signed?.forEach((s) => byPath.set(s.path ?? "", s.signedUrl ?? null));

  return msgs.map((m) => ({
    ...m,
    signed_url: m.attachment_url ? byPath.get(m.attachment_url) ?? null : null,
  }));
};

const decorateMessage = async (msg: ChatMessage): Promise<ChatMessage> => {
  if (!msg.attachment_url) return { ...msg, signed_url: null };
  const { data } = await supabase.storage
    .from(CHAT_BUCKET)
    .createSignedUrl(msg.attachment_url, SIGNED_URL_TTL_SECONDS);
  return { ...msg, signed_url: data?.signedUrl ?? null };
};

export const useChat = (
  projectId: string,
  currentUserId: string,
  currentUserName: string,
  currentUserRole: "client" | "contractor"
) => {
  const [state, setState] = useState<ChatState>({
    messages: [],
    loading: false,
    error: null,
    hasMore: true,
    typingUsers: [],
  });

  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const typingStateRef = useRef<boolean>(false);
  const subscriptionRef = useRef<any>(null);
  const channelRef = useRef<any>(null);

  // Load initial messages
  const loadMessages = useCallback(async () => {
    if (!projectId) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { data, error } = await supabase
        .from("messages")
        .select("id, project_id, sender_id, sender_role, timestamp, text, attachment_url, read_by")
        .eq("project_id", projectId)
        .order("timestamp", { ascending: false })
        .limit(MESSAGES_PAGE_SIZE);

      if (error) throw error;

      const decorated = await decorateMessages((data || []).reverse() as ChatMessage[]);

      setState((prev) => ({
        ...prev,
        messages: decorated,
        hasMore: (data?.length || 0) >= MESSAGES_PAGE_SIZE,
        loading: false,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : "Failed to load messages",
        loading: false,
      }));
    }
  }, [projectId]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!projectId || state.messages.length === 0) return;

    setState((prev) => ({ ...prev, loading: true }));

    try {
      const oldestMessage = state.messages[0];
      const { data, error } = await supabase
        .from("messages")
        .select("id, project_id, sender_id, sender_role, timestamp, text, attachment_url, read_by")
        .eq("project_id", projectId)
        .lt("timestamp", oldestMessage.timestamp)
        .order("timestamp", { ascending: false })
        .limit(MESSAGES_PAGE_SIZE);

      if (error) throw error;

      const decorated = await decorateMessages((data || []).reverse() as ChatMessage[]);

      setState((prev) => ({
        ...prev,
        messages: [...decorated, ...prev.messages],
        hasMore: (data?.length || 0) >= MESSAGES_PAGE_SIZE,
        loading: false,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : "Failed to load more messages",
        loading: false,
      }));
    }
  }, [projectId, state.messages]);



  // Set typing indicator
  const setTyping = useCallback(
    (isTyping: boolean) => {
      if (typingStateRef.current === isTyping) return;

      typingStateRef.current = isTyping;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Publish typing event using the subscribed channel
      if (channelRef.current) {
        channelRef.current.send({
          type: "broadcast",
          event: "typing",
          payload: {
            userId: currentUserId,
            userName: currentUserName,
            projectId,
            isTyping,
          },
        });
      }

      // Auto-disable typing after delay
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          setTyping(false);
        }, TYPING_DEBOUNCE_MS);
      }
    },
    [projectId, currentUserId, currentUserName]
  );

    // Send message
  const sendMessage = useCallback(
    async (content: string, messageType: "text" | "image", file?: File) => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      if (messageType === "text" && !content.trim()) {
        throw new Error("Message content is required");
      }
      if (messageType === "image" && !file) {
        throw new Error("File is required for image messages");
      }

      try {
        let attachmentPath: string | null = null;

        if (file) {
          attachmentPath = `${projectId}/${crypto.randomUUID()}-${file.name}`;
          const { error: uploadError } = await supabase.storage
            .from(CHAT_BUCKET)
            .upload(attachmentPath, file, {
              contentType: file.type || undefined,
              upsert: false,
            });
          if (uploadError) throw uploadError;
        }

        const messageData = {
          project_id: projectId,
          sender_id: currentUserId,
          sender_role: currentUserRole,
          text: messageType === "image" ? "" : content.trim(),
          attachment_url: attachmentPath,
          timestamp: new Date().toISOString(),
          read_by: [currentUserId],
        };

        const { data, error } = await supabase
          .from("messages")
          .insert([messageData])
          .select()
          .single();

        if (error) {
          if (attachmentPath) {
            await supabase.storage.from(CHAT_BUCKET).remove([attachmentPath]).catch(() => {});
          }
          throw error;
        }

        const decorated = await decorateMessage(data as ChatMessage);

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, decorated],
        }));

        setTyping(false);
      } catch (err) {
        throw err instanceof Error ? err : new Error("Failed to send message");
      }
    },
    [projectId, currentUserId, currentUserRole, setTyping]
  );

  // Mark message as read
  const markAsRead = useCallback(
    async (messageId: string) => {
      try {
        // Fetch the message to get current read_by
        const { data: messageData, error: fetchError } = await supabase
          .from("messages")
          .select("read_by")
          .eq("id", messageId)
          .single();

        if (fetchError) throw fetchError;
        
        // Check if already read by current user
        if (messageData?.read_by?.includes(currentUserId)) {
          return;
        }

        const { error } = await supabase
          .from("messages")
          .update({
            read_by: [
              ...(messageData?.read_by || []),
              currentUserId,
            ],
          })
          .eq("id", messageId);

        if (error) throw error;

        // Update local state
        setState((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  read_by: [...(m.read_by || []), currentUserId],
                }
              : m
          ),
        }));
      } catch (err) {
        console.error("Failed to mark message as read:", err);
      }
    },
    [currentUserId]
  );

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!("Notification" in window)) return;

    if (Notification.permission === "granted") return;

    if (Notification.permission !== "denied") {
      try {
        const permission = await Notification.requestPermission();
        return permission === "granted";
      } catch (err) {
        console.error("Failed to request notification permission:", err);
      }
    }
  }, []);

  // Send notification for new message
  const sendNotification = useCallback(
    (message: ChatMessage) => {
      if (
        message.sender_id === currentUserId ||
        Notification.permission !== "granted"
      ) {
        return;
      }

      try {
        const senderName = message.sender_id === currentUserId ? "You" : "New message";
        const body = message.attachment_url ? "📸 Sent an image" : message.text;
        new Notification(`Message from ${senderName}`, {
          body: body || "New message",
          icon: "/logo192.png",
          tag: projectId,
          requireInteraction: false,
        });
      } catch (err) {
        console.error("Failed to send notification:", err);
      }
    },
    [projectId, currentUserId]
  );

  // Setup real-time subscriptions
  useEffect(() => {
    loadMessages();
    requestNotificationPermission();

    // Subscribe to new messages
    const channel = supabase
      .channel(`chat:${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `project_id=eq.${projectId}`,
        },
        async (payload) => {
          const newMessage = payload.new as ChatMessage;
          const decorated = await decorateMessage(newMessage);
          setState((prev) => {
            // Skip if we already appended this message optimistically.
            if (prev.messages.some((m) => m.id === decorated.id)) return prev;
            return { ...prev, messages: [...prev.messages, decorated] };
          });
          sendNotification(decorated);
          markAsRead(decorated.id);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          const updatedMessage = payload.new as ChatMessage;
          setState((prev) => ({
            ...prev,
            messages: prev.messages.map((m) =>
              m.id === updatedMessage.id
                ? { ...updatedMessage, signed_url: m.signed_url }
                : m
            ),
          }));
        }
      )
      .on("broadcast", { event: "typing" }, (payload) => {
        const typingIndicator = payload.payload as TypingIndicator;

        if (typingIndicator.userId === currentUserId) return;

        if (typingIndicator.isTyping) {
          setState((prev) => {
            const existing = prev.typingUsers.find(
              (u) => u.userId === typingIndicator.userId
            );
            if (existing) return prev;

            return {
              ...prev,
              typingUsers: [...prev.typingUsers, typingIndicator],
            };
          });
        } else {
          setState((prev) => ({
            ...prev,
            typingUsers: prev.typingUsers.filter(
              (u) => u.userId !== typingIndicator.userId
            ),
          }));
        }
      })
      .subscribe();

    subscriptionRef.current = channel;
    channelRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [projectId, loadMessages, sendNotification, markAsRead, currentUserId, requestNotificationPermission]);

  // Auto mark messages as read
  useEffect(() => {
    state.messages.forEach((message) => {
      if (
        message.sender_id !== currentUserId &&
        !message.read_by?.includes(currentUserId)
      ) {
        markAsRead(message.id);
      }
    });
  }, [state.messages, currentUserId, markAsRead]);

  return {
    ...state,
    sendMessage,
    setTyping,
    loadMoreMessages,
    markAsRead,
    requestNotificationPermission,
  };
};
